import { useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, FlaskConical } from 'lucide-react';
import { usePropertyContext } from '@/context/PropertyContext';
import { BOOKING_STEPS, type BookingStep } from '@/lib/constants';
import { api, type ExperienceDetail } from '@/lib/api';
import { cn } from '@/lib/utils';
import { DateTimeStep } from './DateTimeStep';
import { GuestStep } from './GuestStep';
import { AddOnsStep } from './AddOnsStep';
import { SummaryStep } from './SummaryStep';
import { PaymentStep } from './PaymentStep';

export interface SelectedAddOn {
  id: string;
  name: string;
  price: number;
  priceType: 'per_person' | 'per_booking';
  quantity: number;
}

export interface BookingData {
  date: string;
  time: string;
  inventoryId: string;
  adultCount: number;
  childCount: number;
  addOns: SelectedAddOn[];
  guestInfo: {
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
  };
  specialRequests: string;
  discountCode: string;
}

/** Extended pricing interface including platform fees */
export interface BookingPricing {
  baseAmount: number;
  childAmount: number;
  addOnsAmount: number;
  discountAmount: number;
  subtotal: number;
  /** Platform service fee (null if hotel absorbs) */
  serviceFee: number | null;
  /** Service fee rate as decimal */
  serviceFeeRate: number | null;
  /** Tax amount */
  taxAmount: number;
  /** Final total guest pays */
  totalAmount: number;
  totalGuests: number;
  /** Fee display mode from organization */
  feeDisplayMode: 'guest_pays' | 'hotel_absorbs';
}

interface BookingFlowProps {
  experience: ExperienceDetail;
}

const STEP_ORDER: BookingStep[] = [
  BOOKING_STEPS.SELECT_DATE,
  BOOKING_STEPS.SELECT_TIME,
  BOOKING_STEPS.GUEST_INFO,
  BOOKING_STEPS.ADD_ONS,
  BOOKING_STEPS.SUMMARY,
  BOOKING_STEPS.PAYMENT,
];

const initialBookingData: BookingData = {
  date: '',
  time: '',
  inventoryId: '',
  adultCount: 1,
  childCount: 0,
  addOns: [],
  guestInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
  },
  specialRequests: '',
  discountCode: '',
};

/**
 * BookingFlow - Premium multi-step booking experience
 * Features elegant step indicator and smooth transitions
 */
export function BookingFlow({ experience }: BookingFlowProps) {
  const navigate = useNavigate();
  const { propertyKey, isDemo, feeSettings } = usePropertyContext();
  const [currentStep, setCurrentStep] = useState<BookingStep>(BOOKING_STEPS.SELECT_DATE);
  const [bookingData, setBookingData] = useState<BookingData>(initialBookingData);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [bookingNumber, setBookingNumber] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Discount tracking
  const [appliedDiscount, setAppliedDiscount] = useState<{
    code: string;
    discountPercent: number;
    discountAmount: number;
  } | null>(null);

  const hasAddOns = experience.addOns && experience.addOns.length > 0;

  // Filter steps if no add-ons available
  const steps = useMemo(() => {
    if (!hasAddOns) {
      return STEP_ORDER.filter(s => s !== BOOKING_STEPS.ADD_ONS);
    }
    return STEP_ORDER;
  }, [hasAddOns]);

  const currentStepIndex = steps.indexOf(currentStep);

  const updateBookingData = useCallback((data: Partial<BookingData>) => {
    setBookingData(prev => ({ ...prev, ...data }));
    setError(null);
  }, []);

  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < steps.length) {
      setCurrentStep(steps[nextIndex]);
    }
  }, [currentStepIndex, steps]);

  const goToPrevStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(steps[prevIndex]);
    }
  }, [currentStepIndex, steps]);

  // Discount handlers
  const handleApplyDiscount = useCallback((code: string, discountPercent: number, discountAmount: number) => {
    setAppliedDiscount({ code, discountPercent, discountAmount });
    setBookingData(prev => ({ ...prev, discountCode: code }));
  }, []);

  const handleRemoveDiscount = useCallback(() => {
    setAppliedDiscount(null);
    setBookingData(prev => ({ ...prev, discountCode: '' }));
  }, []);

  const handleCreateBooking = async () => {
    if (!propertyKey) return;
    
    setIsLoading(true);
    setError(null);

    try {
      const result = await api.createBooking(propertyKey, {
        experienceId: experience.id,
        date: bookingData.date,
        time: bookingData.time,
        guestCount: bookingData.adultCount + bookingData.childCount,
        guestInfo: bookingData.guestInfo,
        addOns: bookingData.addOns.map(a => ({ id: a.id, quantity: a.quantity })),
        discountCode: bookingData.discountCode || undefined,
      });

      setBookingId(result.bookingId);
      setBookingNumber(result.bookingNumber);
      goToNextStep();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create booking');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate pricing including platform fees
  const pricing: BookingPricing = useMemo(() => {
    const totalGuests = bookingData.adultCount + bookingData.childCount;
    const baseAmount = experience.priceType === 'per_group' 
      ? experience.basePrice 
      : experience.basePrice * bookingData.adultCount;
    
    const childAmount = 0;

    const addOnsAmount = bookingData.addOns.reduce((sum, addon) => {
      if (addon.priceType === 'per_person') {
        return sum + (addon.price * addon.quantity * totalGuests);
      }
      return sum + (addon.price * addon.quantity);
    }, 0);

    const subtotal = baseAmount + childAmount + addOnsAmount;
    const discountAmount = 0;
    const subtotalAfterDiscount = subtotal - discountAmount;
    
    const { feeRate, feeDisplayMode, showFeeToGuest } = feeSettings;
    const calculatedFee = Math.round(subtotalAfterDiscount * feeRate);
    
    const serviceFee = showFeeToGuest ? calculatedFee : null;
    const serviceFeeRate = showFeeToGuest ? feeRate : null;
    
    const taxAmount = 0;
    
    const totalAmount = showFeeToGuest 
      ? subtotalAfterDiscount + calculatedFee + taxAmount
      : subtotalAfterDiscount + taxAmount;
    
    return {
      baseAmount,
      childAmount,
      addOnsAmount,
      discountAmount,
      subtotal: subtotalAfterDiscount,
      serviceFee,
      serviceFeeRate,
      taxAmount,
      totalAmount,
      totalGuests,
      feeDisplayMode,
    };
  }, [bookingData, experience, feeSettings]);

  const stepLabels: Record<BookingStep, string> = {
    [BOOKING_STEPS.SELECT_DATE]: 'Date',
    [BOOKING_STEPS.SELECT_TIME]: 'Time',
    [BOOKING_STEPS.GUEST_INFO]: 'Details',
    [BOOKING_STEPS.ADD_ONS]: 'Add-ons',
    [BOOKING_STEPS.SUMMARY]: 'Review',
    [BOOKING_STEPS.PAYMENT]: 'Payment',
    [BOOKING_STEPS.SUCCESS]: 'Done',
  };

  return (
    <div className="mx-auto max-w-2xl animate-fade-in">
      {/* Demo Banner - Refined */}
      {isDemo && (
        <div className="mb-6 rounded-xl border border-warning/30 bg-gradient-to-r from-warning/5 to-warning/10 p-4 shadow-soft">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-warning/15">
              <FlaskConical className="h-5 w-5 text-warning" />
            </div>
            <div>
              <p className="font-semibold text-warning-foreground">Demo Mode</p>
              <p className="mt-0.5 text-sm text-warning-foreground/80">
                This is a demo booking. No payment will be processed.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Premium Step Progress Indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          {steps.map((step, index) => (
            <div key={step} className="flex items-center">
              {/* Step Circle */}
              <div className="relative">
                <div className={cn(
                  'flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold',
                  'transition-all duration-500 ease-out',
                  // Completed step
                  index < currentStepIndex && 'bg-primary text-primary-foreground shadow-sm',
                  // Current step - with glow effect
                  index === currentStepIndex && 'bg-primary text-primary-foreground shadow-gold-glow',
                  // Future step
                  index > currentStepIndex && 'bg-muted text-muted-foreground'
                )}>
                  {index < currentStepIndex ? (
                    <Check className="h-5 w-5" strokeWidth={2.5} />
                  ) : (
                    <span>{index + 1}</span>
                  )}
                </div>
                
                {/* Pulse animation for current step */}
                {index === currentStepIndex && (
                  <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" style={{ animationDuration: '2s' }} />
                )}
              </div>
              
              {/* Connector Line */}
              {index < steps.length - 1 && (
                <div className="relative mx-2 h-0.5 w-8 sm:w-12 md:w-16 lg:w-20 overflow-hidden rounded-full bg-muted">
                  {/* Animated fill */}
                  <div 
                    className={cn(
                      'absolute inset-y-0 left-0 bg-primary rounded-full',
                      'transition-all duration-500 ease-out'
                    )}
                    style={{ 
                      width: index < currentStepIndex ? '100%' : '0%' 
                    }}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
        
        {/* Step Labels */}
        <div className="mt-3 flex justify-between">
          {steps.map((step, index) => (
            <span 
              key={step} 
              className={cn(
                'text-xs font-medium transition-colors duration-300',
                'w-10 text-center',
                index <= currentStepIndex ? 'text-foreground' : 'text-muted-foreground'
              )}
            >
              {stepLabels[step]}
            </span>
          ))}
        </div>
      </div>

      {/* Error Display - Premium styling */}
      {error && (
        <div className="mb-6 rounded-xl border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive animate-slide-up">
          <p className="font-medium">{error}</p>
        </div>
      )}

      {/* Step Content - Premium card */}
      <div className="rounded-2xl border border-border/50 bg-card shadow-luxury overflow-hidden">
        {currentStep === BOOKING_STEPS.SELECT_DATE && (
          <DateTimeStep
            experience={experience}
            bookingData={bookingData}
            onUpdate={updateBookingData}
            onNext={goToNextStep}
            mode="date"
          />
        )}

        {currentStep === BOOKING_STEPS.SELECT_TIME && (
          <DateTimeStep
            experience={experience}
            bookingData={bookingData}
            onUpdate={updateBookingData}
            onNext={goToNextStep}
            onBack={goToPrevStep}
            mode="time"
          />
        )}

        {currentStep === BOOKING_STEPS.GUEST_INFO && (
          <GuestStep
            experience={experience}
            bookingData={bookingData}
            onUpdate={updateBookingData}
            onNext={goToNextStep}
            onBack={goToPrevStep}
          />
        )}

        {currentStep === BOOKING_STEPS.ADD_ONS && hasAddOns && (
          <AddOnsStep
            experience={experience}
            bookingData={bookingData}
            onUpdate={updateBookingData}
            onNext={goToNextStep}
            onBack={goToPrevStep}
          />
        )}

        {currentStep === BOOKING_STEPS.SUMMARY && (
          <SummaryStep
            experience={experience}
            bookingData={bookingData}
            pricing={pricing}
            onUpdate={updateBookingData}
            onConfirm={handleCreateBooking}
            onBack={goToPrevStep}
            isLoading={isLoading}
            appliedDiscount={appliedDiscount}
            onApplyDiscount={handleApplyDiscount}
            onRemoveDiscount={handleRemoveDiscount}
          />
        )}

        {currentStep === BOOKING_STEPS.PAYMENT && bookingId && (
          <PaymentStep
            bookingId={bookingId}
            bookingNumber={bookingNumber || ''}
            experience={experience}
            bookingData={bookingData}
            pricing={pricing}
            isDemo={isDemo}
            onSuccess={() => {
              const params = new URLSearchParams({
                booking: bookingNumber || '',
                experienceId: experience.id,
                experienceName: experience.name,
                amount: String(pricing.totalAmount),
                currency: experience.currency || 'USD',
                ...(isDemo && { demo: 'true' }),
              });
              navigate(`/success?${params.toString()}`);
            }}
            onBack={goToPrevStep}
          />
        )}
      </div>
    </div>
  );
}
